import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { Container, FormFooter, FormInput, Navigation, NavBar, Select } from '../../components';
import { COLOR, FONT } from '../../../constants/styles';
import { appointmentLabelForType } from '../scheduler/common_views';
import {
  getSchedulerNotificationReasonOptions,
  REJECT_REASON_ANY_OPTION_ID,
  REJECT_REASON_ANY_OPTION_VALUE,
} from '../scheduler/reject_reason_options';
import {
  resetNotificationMessagesStatus,
  saveSchedulerNotificationMessage as saveSchedulerNotificationMessageAction,
} from './slice';
import { EVENT_TYPE_READINESS } from '../../../constants/eventTypes';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';

const EMAIL_MESSAGE_MAX_LENGTH = 500;
const SMS_MESSAGE_MAX_LENGTH = 300;
const SAVE_SUCCESS_MESSAGE = 'Saved, you can reuse the existing text for a new set of messages.';

const trimText = (value) => value?.trim();

const DEFAULT_CONFIRMED_MESSAGE = 'Appointment confirmed on ${DATE} at ${TIME}.';
const DEFAULT_UNAVAILABLE_MESSAGE =
  'Your appointment on ${DATE} at ${TIME} is unavailable. Please re-visit the Outpatient App scheduling website to try again.';

const getPersistedReason = (message) => {
  if (message?.reasonOptionId != null) {
    return message.reasonOptionId === REJECT_REASON_ANY_OPTION_ID
      ? REJECT_REASON_ANY_OPTION_VALUE
      : trimText(message.reasonOptionId) || REJECT_REASON_ANY_OPTION_VALUE;
  }

  const persistedReason = trimText(message?.reason);

  if (!persistedReason) {
    return REJECT_REASON_ANY_OPTION_VALUE;
  }

  const hasMatchingReasonOption = getSchedulerNotificationReasonOptions(
    message?.appointmentType || null
  ).some((option) => option.id === persistedReason);

  return hasMatchingReasonOption ? persistedReason : REJECT_REASON_ANY_OPTION_VALUE;
};

const toReasonFormState = (appointmentType, reason) => {
  const persistedReason = trimText(reason) || '';

  if (!persistedReason) {
    return {
      reasonOptionId: REJECT_REASON_ANY_OPTION_ID,
    };
  }

  const matchingOption = getSchedulerNotificationReasonOptions(appointmentType).find(
    (option) => option.id === persistedReason
  );

  if (matchingOption) {
    return {
      reasonOptionId: matchingOption.id,
    };
  }

  return {
    reasonOptionId: REJECT_REASON_ANY_OPTION_ID,
  };
};

const normalizeReasonFields = (formState) => {
  if (formState.appointmentType == null && formState.masterDepartmentId == null) {
    return {
      ...formState,
      reasonOptionId: REJECT_REASON_ANY_OPTION_ID,
    };
  }

  const hasMatchingReasonOption =
    formState.reasonOptionId === REJECT_REASON_ANY_OPTION_ID ||
    getSchedulerNotificationReasonOptions(formState.appointmentType).some(
      (option) => option.id === formState.reasonOptionId
    );

  return {
    ...formState,
    reasonOptionId: hasMatchingReasonOption
      ? formState.reasonOptionId || REJECT_REASON_ANY_OPTION_ID
      : REJECT_REASON_ANY_OPTION_ID,
  };
};

const isDefaultSchedulerNotificationMessage = (message) => {
  return !message?.appointmentType && !message?.masterDepartmentId && !getPersistedReason(message);
};

const limitSmsMessage = (value) => {
  return typeof value === 'string' ? value.slice(0, SMS_MESSAGE_MAX_LENGTH) : '';
};

const limitEmailMessage = (value) => {
  return typeof value === 'string' ? value.slice(0, EMAIL_MESSAGE_MAX_LENGTH) : '';
};

const getNextFieldValue = (field, value) => {
  if (field === 'emailConfirmation' || field === 'emailUnavailable') {
    return limitEmailMessage(value);
  }

  if (field === 'smsConfirmation' || field === 'smsUnavailable') {
    return limitSmsMessage(value);
  }

  if (field === 'reasonOptionId') {
    return value || REJECT_REASON_ANY_OPTION_ID;
  }

  return value;
};

const toFormState = (message) => {
  return {
    appointmentType: message?.appointmentType || null,
    ...toReasonFormState(message?.appointmentType || null, message?.reason),
    masterDepartmentId: message?.masterDepartmentId ?? null,
    emailConfirmation: limitEmailMessage(message?.emailConfirmation) || DEFAULT_CONFIRMED_MESSAGE,
    emailUnavailable: limitEmailMessage(message?.emailUnavailable) || DEFAULT_UNAVAILABLE_MESSAGE,
    smsConfirmation: limitSmsMessage(message?.smsConfirmation) || DEFAULT_CONFIRMED_MESSAGE,
    smsUnavailable: limitSmsMessage(message?.smsUnavailable) || DEFAULT_UNAVAILABLE_MESSAGE,
  };
};

const SchedulerNotificationMessagesForm = memo((props) => {
  useEffect(NavBar.setup(props), []);

  const dispatch = useDispatch();
  const employerId = props.employer?.employerId;
  const mode = props.mode || 'add';
  const isEditMode = mode === 'edit';
  const occHealthDepartments = useSelector(
    (state) => state.screens.employer.occHealthDepartmentsData
  );
  const messages = useSelector(
    (state) => state.screens.airforceSetup.notificationMessagesData || []
  );
  const saveNotificationMessageStatus = useSelector(
    (state) => state.screens.airforceSetup.saveNotificationMessageStatus
  );
  const saveNotificationMessageError = useSelector(
    (state) => state.screens.airforceSetup.saveNotificationMessageError
  );

  const [focusedSelect, setFocusedSelect] = useState(null);
  const [formState, setFormState] = useState(() => toFormState(props.item));

  const fieldChangeHandlersRef = useRef({});
  const fieldFocusedHandlersRef = useRef({});

  useEffect(() => {
    dispatch(resetNotificationMessagesStatus());
    return () => {
      dispatch(resetNotificationMessagesStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    setFormState(toFormState(props.item));
  }, [props.item]);

  const visibleScheduleEventTypes = useMemo(() => {
    return (props.employer?.eventTypesConfiguration?.eventTypesAdmin || []).filter(
      (eventType) => eventType !== EVENT_TYPE_READINESS
    );
  }, [props.employer?.eventTypesConfiguration?.eventTypesAdmin]);
  const isSaving = saveNotificationMessageStatus === REQUESTING;

  const departmentOptions = useMemo(() => {
    const optionsById = Object.values(occHealthDepartments || {}).reduce((result, department) => {
      if (department?.master_department_id && department?.name) {
        result[department.master_department_id] = {
          id: department.master_department_id,
          label: department.name,
        };
      }
      return result;
    }, {});

    return [{ id: null, label: 'All departments' }].concat(
      Object.values(optionsById).sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [occHealthDepartments]);

  const appointmentTypeOptions = useMemo(() => {
    const additionalTypes = messages
      .map((message) => message.appointmentType)
      .filter((eventType) => !!eventType && !visibleScheduleEventTypes.includes(eventType));
    const allTypes = Array.from(new Set([...visibleScheduleEventTypes, ...additionalTypes]));

    return [{ id: null, label: 'All appointment types' }].concat(
      allTypes.map((eventType) => ({
        id: eventType,
        label: appointmentLabelForType(eventType),
      }))
    );
  }, [messages, visibleScheduleEventTypes]);

  const selectedAppointmentTypeOption = useMemo(() => {
    return (
      appointmentTypeOptions.find((option) => option.id === formState.appointmentType) ||
      appointmentTypeOptions[0] ||
      null
    );
  }, [appointmentTypeOptions, formState.appointmentType]);

  const selectedDepartmentOption = useMemo(() => {
    return (
      departmentOptions.find((option) => option.id === formState.masterDepartmentId) ||
      departmentOptions[0] ||
      null
    );
  }, [departmentOptions, formState.masterDepartmentId]);

  const reasonOptions = useMemo(() => {
    return getSchedulerNotificationReasonOptions(formState.appointmentType);
  }, [formState.appointmentType]);

  const selectedReasonOption = useMemo(() => {
    return (
      reasonOptions.find((option) => option.id === formState.reasonOptionId) ||
      reasonOptions[0] ||
      null
    );
  }, [formState.reasonOptionId, reasonOptions]);

  const persistedReason = useMemo(() => {
    return getPersistedReason(formState);
  }, [formState]);

  const validationMessage = useMemo(() => {
    const hasAllMessagesConfigured = [
      formState.emailConfirmation,
      formState.emailUnavailable,
      formState.smsConfirmation,
      formState.smsUnavailable,
    ].every((value) => !!trimText(value));

    if (!hasAllMessagesConfigured) {
      return 'Set all confirmation and unavailable messages before saving.';
    }

    return null;
  }, [formState, isEditMode]);

  const isNotDefaultSetOfMessages = useMemo(() => {
    return !isDefaultSchedulerNotificationMessage(formState);
  }, [formState]);

  const fieldChangedFactory = useCallback((field) => {
    if (!fieldChangeHandlersRef.current[field]) {
      fieldChangeHandlersRef.current[field] = (value) => {
        setFormState((current) => {
          return normalizeReasonFields({
            ...current,
            [field]: getNextFieldValue(field, value),
          });
        });
      };
    }
    return fieldChangeHandlersRef.current[field];
  }, []);

  const fieldFocusedFactory = useCallback((field) => {
    if (!fieldFocusedHandlersRef.current[field]) {
      fieldFocusedHandlersRef.current[field] = () => {
        setFocusedSelect(field);
      };
    }
    return fieldFocusedHandlersRef.current[field];
  }, []);

  const handleSelectBlur = useCallback(() => {
    setFocusedSelect(null);
  }, []);

  const handleCancel = useCallback(() => {
    Navigation.close(props);
  }, [props]);

  const handleSave = useCallback(() => {
    const payload = {
      employerId,
      appointmentType: formState.appointmentType || undefined,
      reason: persistedReason,
      masterDepartmentId: formState.masterDepartmentId ?? undefined,
      emailConfirmation: trimText(limitEmailMessage(formState.emailConfirmation)),
      emailUnavailable: trimText(limitEmailMessage(formState.emailUnavailable)),
      smsConfirmation: trimText(limitSmsMessage(formState.smsConfirmation)),
      smsUnavailable: trimText(limitSmsMessage(formState.smsUnavailable)),
      ...(isEditMode && props.item?.schedulerMessageId != null
        ? {
            schedulerMessageId: props.item.schedulerMessageId,
          }
        : {}),
    };

    dispatch(saveSchedulerNotificationMessageAction({ employerId, message: payload }));
  }, [
    dispatch,
    employerId,
    formState,
    isEditMode,
    persistedReason,
    props.item?.schedulerMessageId,
  ]);

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      onSave={handleSave}
      cancelLabel="Cancel"
      saveLabel={isSaving ? 'Saving…' : 'Save'}
      saveDisabled={isSaving || !!validationMessage}
    />
  );

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      contentContainerStyle={styles.contentContainer}
      renderActivityOverlay={isSaving}
      successMessage={saveNotificationMessageStatus === SUCCESS ? SAVE_SUCCESS_MESSAGE : null}
      errorMessage={saveNotificationMessageError?.message || null}
    >
      <Text style={styles.helperText}>
        <Text style={styles.codeText}>${'{DATE}'}</Text> and{' '}
        <Text style={styles.codeText}>${'{TIME}'}</Text> in messages will be replaced by the date
        and time of the appointment.
      </Text>

      <View
        style={[
          styles.formRow,
          focusedSelect === 'appointmentType' || focusedSelect === 'masterDepartmentId'
            ? styles.focusedFormRow
            : null,
        ]}
      >
        <View
          style={[
            styles.selectField,
            focusedSelect === 'appointmentType' ? styles.focusedSelectField : null,
          ]}
        >
          <Select
            required={true}
            editable={!isEditMode}
            closeOnSelect={true}
            webMode={true}
            label="Appointment Type"
            options={appointmentTypeOptions}
            value={selectedAppointmentTypeOption}
            onSelect={fieldChangedFactory('appointmentType')}
            onFocus={fieldFocusedFactory('appointmentType')}
            onBlur={handleSelectBlur}
            containerStyle={styles.selectFieldContainer}
            nativeContainerStyle={styles.selectFieldContainer}
            menuPlacement="bottom"
          />
        </View>
        <View
          style={[
            styles.selectField,
            focusedSelect === 'masterDepartmentId' ? styles.focusedSelectField : null,
          ]}
        >
          <Select
            required={true}
            editable={!isEditMode}
            closeOnSelect={true}
            webMode={true}
            label="Occupational Health Department"
            options={departmentOptions}
            value={selectedDepartmentOption}
            onSelect={fieldChangedFactory('masterDepartmentId')}
            onFocus={fieldFocusedFactory('masterDepartmentId')}
            onBlur={handleSelectBlur}
            containerStyle={styles.selectFieldContainer}
            nativeContainerStyle={styles.selectFieldContainer}
            menuPlacement="bottom"
          />
        </View>
      </View>

      <View style={styles.formRow}>
        <View style={styles.messageField}>
          <FormInput
            required={true}
            controlled={true}
            multiline={true}
            numberOfLines={4}
            label="Email Confirmation"
            value={formState.emailConfirmation}
            maxLength={EMAIL_MESSAGE_MAX_LENGTH}
            onChangeText={fieldChangedFactory('emailConfirmation')}
            containerStyle={styles.messageInputField}
            testID="scheduler-notification-email-confirmation-input"
            inputStyle={{ height: 100 }}
          />
          <Text
            style={styles.characterCountText}
            testID="scheduler-notification-email-confirmation-counter"
          >
            {formState.emailConfirmation.length}/{EMAIL_MESSAGE_MAX_LENGTH}
          </Text>
        </View>
        <View style={styles.messageField}>
          <FormInput
            required={true}
            controlled={true}
            multiline={true}
            numberOfLines={4}
            label="Email Unavailable"
            value={formState.emailUnavailable}
            maxLength={EMAIL_MESSAGE_MAX_LENGTH}
            onChangeText={fieldChangedFactory('emailUnavailable')}
            containerStyle={styles.messageInputField}
            testID="scheduler-notification-email-unavailable-input"
            inputStyle={{ height: 100 }}
          />
          <Text
            style={styles.characterCountText}
            testID="scheduler-notification-email-unavailable-counter"
          >
            {formState.emailUnavailable.length}/{EMAIL_MESSAGE_MAX_LENGTH}
          </Text>
        </View>
      </View>

      <View style={styles.formRow}>
        <View style={styles.messageField}>
          <FormInput
            required={true}
            controlled={true}
            multiline={true}
            numberOfLines={4}
            label="SMS Confirmation"
            value={formState.smsConfirmation}
            maxLength={SMS_MESSAGE_MAX_LENGTH}
            onChangeText={fieldChangedFactory('smsConfirmation')}
            containerStyle={styles.messageInputField}
            testID="scheduler-notification-sms-confirmation-input"
            inputStyle={{ height: 100 }}
          />
          <Text
            style={styles.characterCountText}
            testID="scheduler-notification-sms-confirmation-counter"
          >
            {formState.smsConfirmation.length}/{SMS_MESSAGE_MAX_LENGTH}
          </Text>
        </View>
        <View style={styles.messageField}>
          <FormInput
            required={true}
            controlled={true}
            multiline={true}
            numberOfLines={4}
            label="SMS Unavailable"
            value={formState.smsUnavailable}
            maxLength={SMS_MESSAGE_MAX_LENGTH}
            onChangeText={fieldChangedFactory('smsUnavailable')}
            containerStyle={styles.messageInputField}
            testID="scheduler-notification-sms-unavailable-input"
            inputStyle={{ height: 100 }}
          />
          <Text
            style={styles.characterCountText}
            testID="scheduler-notification-sms-unavailable-counter"
          >
            {formState.smsUnavailable.length}/{SMS_MESSAGE_MAX_LENGTH}
          </Text>
        </View>
      </View>

      {isNotDefaultSetOfMessages && (
        <View
          style={[
            styles.formRow,
            styles.reasonRow,
            focusedSelect === 'reasonOptionId' ? styles.focusedFormRow : null,
          ]}
        >
          <View style={styles.messageField} />
          <View style={[styles.messageField, styles.reasonField]}>
            <View
              style={[
                styles.selectField,
                focusedSelect === 'reasonOptionId' ? styles.focusedSelectField : null,
              ]}
            >
              <Select
                closeOnSelect={true}
                webMode={true}
                label="Rejection reason"
                options={reasonOptions}
                value={selectedReasonOption}
                onSelect={fieldChangedFactory('reasonOptionId')}
                onFocus={fieldFocusedFactory('reasonOptionId')}
                onBlur={handleSelectBlur}
                containerStyle={styles.selectFieldContainer}
                nativeContainerStyle={styles.selectFieldContainer}
              />
            </View>
          </View>
        </View>
      )}

      {!!validationMessage && <Text style={styles.validationText}>{validationMessage}</Text>}
    </Container>
  );
});

const styles = StyleSheet.create({
  contentContainer: {
    padding: 20,
  },
  helperText: {
    ...FONT.regular,
    color: COLOR.text,
    marginBottom: 16,
    padding: 5,
  },
  codeText: {
    ...FONT.semibold,
  },
  formRow: {
    flexDirection: 'row',
    gap: 20,
  },
  focusedFormRow: {
    zIndex: 1020,
  },
  selectField: {
    flex: 1,
  },
  focusedSelectField: {
    zIndex: 1000,
  },
  selectFieldContainer: {
    flex: 1,
  },
  reasonRow: {
    marginTop: 8,
  },
  reasonField: {
    marginTop: 8,
    marginBottom: 10,
  },
  messageField: {
    flex: 1,
  },
  messageInputField: {
    marginBottom: 0,
  },
  characterCountText: {
    ...FONT.regular,
    color: COLOR.textLight,
    fontSize: 12,
    alignSelf: 'flex-end',
    marginTop: -8,
    marginBottom: 10,
  },
  validationText: {
    ...FONT.regular,
    color: COLOR.error,
    marginTop: 10,
    alignSelf: 'flex-end',
  },
});

export default SchedulerNotificationMessagesForm;
